import React from "react";

/**
 * Iter 33 — Journaling Streak badge + 7-day dots.
 * Compact strip showing the current streak with a flame, the freeze status,
 * and a row of 7 dots for the last week. Renders inside any container.
 */
export function StreakBadge({ streak }) {
  if (!streak) return null;
  const cur = streak.current_streak || 0;
  const dots = Array.isArray(streak.week_dots) ? streak.week_dots : [];
  const atRisk = !!streak.at_risk;
  const freezeAvailable = !!streak.freeze_available;
  const freezeUsed = !!streak.freeze_used;

  return (
    <section
      data-testid="streak-section"
      className="bt-card p-5 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span
            data-testid="streak-flame"
            aria-hidden="true"
            className="text-3xl"
            style={{ filter: cur > 0 ? "drop-shadow(0 0 6px rgba(255,140,0,0.5))" : "grayscale(1) opacity(0.5)" }}
          >
            🔥
          </span>
          <div>
            <p className="bt-label">Journaling Streak</p>
            <p
              data-testid="streak-count"
              className="font-heading text-3xl bt-num leading-none mt-1"
              style={{ color: cur > 0 ? "var(--bt-accent)" : "var(--bt-text-muted)" }}
            >
              {cur}<span className="text-sm text-[var(--bt-text-muted)] font-normal">{" "}day{cur === 1 ? "" : "s"}</span>
            </p>
          </div>
        </div>

        {/* Freeze chip */}
        {freezeAvailable && !freezeUsed && (
          <span
            data-testid="streak-freeze-available"
            className="text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1"
            style={{
              borderColor: "var(--bt-border)",
              background: "var(--bt-surface-2)",
              color: "var(--bt-text-muted)",
            }}
          >
            ❄️ 1 freeze available
          </span>
        )}
        {freezeUsed && (
          <span
            data-testid="streak-freeze-used"
            className="text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1"
            style={{
              borderColor: "color-mix(in srgb, var(--bt-warn) 40%, var(--bt-border))",
              background: "color-mix(in srgb, var(--bt-warn) 10%, transparent)",
              color: "var(--bt-warn)",
            }}
          >
            ❄️ freeze used
          </span>
        )}
      </div>

      {/* 7-day dot strip */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="streak-week-dots">
        {dots.map((d) => (
          <div
            key={d.date}
            data-testid={`streak-dot-${d.date}`}
            title={d.date + (d.active ? " · logged" : " · skipped")}
            className={`flex flex-col items-center ${d.is_today ? "" : ""}`}
          >
            <span
              className="block rounded-full"
              style={{
                width: 14, height: 14,
                background: d.active
                  ? "var(--bt-accent)"
                  : "color-mix(in srgb, var(--bt-text-muted) 25%, transparent)",
                border: d.is_today ? "2px solid var(--bt-text)" : "none",
                boxShadow: d.active ? "0 0 6px color-mix(in srgb, var(--bt-accent) 50%, transparent)" : "none",
              }}
              aria-label={d.date + (d.active ? " logged" : " skipped")}
            />
            <span className="text-[9px] text-[var(--bt-text-muted)] mt-1 font-mono">
              {new Date(d.date).toLocaleDateString(undefined, { weekday: "narrow" })}
            </span>
          </div>
        ))}
      </div>

      {atRisk && cur > 0 && (
        <p
          data-testid="streak-at-risk-msg"
          className="text-xs"
          style={{ color: "var(--bt-warn)" }}
        >
          Log today to keep your <span className="bt-num">{cur}-day</span> streak alive.
        </p>
      )}
    </section>
  );
}

/**
 * Iter 33 — Next Best Action nudge card.
 * Doctor-tone, one clear action. Color via design-system tokens.
 */
export function NextBestActionCard({ nba }) {
  if (!nba) return null;
  const toneColor = {
    warn: "var(--bt-warn)",
    info: "var(--bt-accent)",
    good: "var(--bt-win)",
  }[nba.tone] || "var(--bt-accent)";
  const toneBgPct = nba.tone === "warn" ? 12 : 10;

  return (
    <section
      data-testid="nba-section"
      className="bt-card p-5"
      style={{
        background: `color-mix(in srgb, ${toneColor} ${toneBgPct}%, var(--bt-surface))`,
        borderColor: `color-mix(in srgb, ${toneColor} 40%, var(--bt-border))`,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-full"
          style={{
            width: 48, height: 48,
            background: `color-mix(in srgb, ${toneColor} 18%, transparent)`,
            border: `1px solid color-mix(in srgb, ${toneColor} 40%, transparent)`,
            fontSize: 24,
          }}
          aria-hidden="true"
        >
          {nba.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="bt-label" style={{ color: toneColor }}>
            Next Best Action
          </p>
          <p
            data-testid="nba-title"
            className="font-heading text-xl mt-1"
            style={{ color: "var(--bt-text)" }}
          >
            {nba.title}
          </p>
          <p
            data-testid="nba-message"
            className="text-sm mt-1.5 leading-relaxed"
            style={{ color: "var(--bt-text-muted)" }}
          >
            {nba.message}
          </p>
        </div>
      </div>
    </section>
  );
}

export default StreakBadge;
